import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { GridPagination } from "@mui/x-data-grid";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import { BiEdit } from "react-icons/bi";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineClear } from "react-icons/md";
import dayjs from "dayjs";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import { Invoice, setSearchBody } from "../../../../../app/slices/invoiceSlice";
import { SelectSupplier } from "../../../../../components/Selectors/SelectSupplier";
import { SelectProducts } from "../../../../../components/Selectors/SelectProducts";
import Permission from "../../../../../components/userPermission/userPermission";

const defaultSearch = {
  supplier_id: null,
  product_id: null,
  serial_number: "",
  start_date: dayjs().format("2024-01-01"),
  end_date: dayjs().format("YYYY-MM-DD"),
};

const InvoiceTable = () => {
  const dispatch = useDispatch();

  const _Invoices = useSelector((state) => state.invoice.invoices);
  const _SearchBody = useSelector((state) => state.invoice.searchBody);
  const _Supplier = useSelector((state) => state.supplier.suppliersList.data);
  const _Products = useSelector((state) => state.product.products.data);

  const [search, setSearch] = useState(defaultSearch);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          القيمة الكلية : {_Invoices.total_amount?.toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "id",
      headerName: "رقم الفاتورة",
      flex: 0.7,
    },
    {
      field: "supplier_name",
      headerName: "المجهز",
      flex: 1,
    },
    {
      field: "items_count",
      headerName: "عدد المواد",
      flex: 0.7,
    },
    {
      field: "discount",
      headerName: "الخصم (IQD)",
      flex: 1,
      renderCell: (params) => parseInt(params.row.discount)?.toLocaleString(),
    },
    {
      field: "total_amount",
      headerName: "المبلغ الكلي (IQD)",
      flex: 1,
      renderCell: (params) =>
        parseInt(params.row.total_amount)?.toLocaleString(),
    },
    {
      field: "created_by_name",
      headerName: "انشاءت بواسطة",
      flex: 1,
    },
    {
      field: "created_at",
      headerName: "تاريخ الانشاء",
      flex: 1,
      renderCell: (params) => dayjs(params.row.created_at).format("YYYY-MM-DD"),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 0.7,
      renderCell: (params) => (
        <Permission permission="invoice_manager">
          <Tooltip title="تعديل">
            <IconButton
              onClick={() => dispatch(Invoice.getById(params.row.id))}
            >
              <BiEdit />
            </IconButton>
          </Tooltip>
        </Permission>
      ),
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(Invoice.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        supplier_id: search.supplier_id ?? "",
        product_id: search.product_id ?? "",
        serial_number: search.serial_number,
        start_date: search.start_date ?? "",
        end_date: search.end_date ?? "",
        skip: currentPage * pageSize,
        take: pageSize,
      })
    );
  }, [currentPage, dispatch, pageSize, search]);

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={3}>
            <SelectSupplier
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  supplier_id: newValue ? newValue.id : null,
                });
              }}
              value={
                _Supplier.filter((x) => x.id === search.supplier_id)[0] ?? null
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SelectProducts
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  product_id: newValue ? newValue.id : null,
                });
              }}
              value={
                _Products.filter((x) => x.id === search.product_id)[0] ?? null
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              size="small"
              value={search.serial_number}
              onChange={(e) => {
                setSearch({ ...search, serial_number: e.target.value });
              }}
              label="الرقم التسلسلي"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateRangepicker
              startDate={search.start_date}
              endDate={search.end_date}
              handleOnChange={(searchDate) => {
                setSearch({
                  ...search,
                  start_date: dayjs(searchDate[0].startDate).format(
                    "YYYY-MM-DD"
                  ),
                  end_date: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                });
              }}
            />
          </Grid>
          <Grid item xs="auto" md={1}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch(defaultSearch);
                  setPageSize(15);
                  setCurrentPage(0);
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: { xs: "auto", md: "63vh" }, width: "100%" }}>
              <ResponsiveDataGrid
                gridHeight={null}
                rows={
                  _Invoices.data?.map((x, i) => ({
                    ...x,
                    index: i + 1 + pageSize * currentPage,
                  })) ?? []
                }
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowCount={_Invoices.total}
                loading={_Invoices.loading}
                rowHeight={50}
                pagination
                paginationMode="server"
                selectionModel={[]}
                onPageChange={(newPage) => setCurrentPage(newPage)}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                components={{
                  LoadingOverlay: LinearProgress,
                  Footer: CustomFooter,
                }}
                mobile={{
                  titleField: "supplier_name",
                  highlightField: "total_amount",
                  highlightUnit: "IQD",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default InvoiceTable;
